import type { THosStatus } from "hos-tools";
import constants from "../constants";
import { DutyStatusLogService } from "../model/DutyStatusLogs";
import { DataActions } from "../store/data/actions";
import { DriveStore } from "../store/reducers";
import { toDateTime } from "../utils/date";
import { FeatureFlagName } from "../utils/featureFlagName";
import { event, log, Event, Source } from "../utils/logger";
import { shouldAddExtraLogging } from "../utils/logger.internal";
import { getServerDate } from "../utils/storage/date";
import { UserService } from "../utils/users";
import Api from "./api";
import Service from "./base";
import type { IFeatureFlagsService } from "./featureFlags";

export type TExpiryBlockedBy = "not-expired" | "not-current-ui-user" | "current-page" | "driving" | "iam-user";
interface IEarlyExpiryData {
    earlyExpiryDate?: Date;
    blockedBy?: TExpiryBlockedBy;
    currentPage?: string;
    currentStatus?: THosStatus | "";
}
type TEarlyExpiryInformation = [shouldExpireSession: boolean, data: IEarlyExpiryData];

// Pages that contain these fragments (ie. ui/inspection/main) will also be protected from session expiry
const PROTECTED_PAGES = [
    "ui/login",
    "ui/workflow",
    "ui/inspection"
];
const protectedPagesRegex = new RegExp(`(${ PROTECTED_PAGES.map(p => p.replace("/", "\/")).join("|") })`);

export class Session extends Service {
    private readonly user: IUser | null;

    constructor(
        private readonly store: DriveStore,
        private readonly api: Api,
        private readonly userService: UserService,
        private readonly dutyStatusLogService: DutyStatusLogService,
        private readonly featureFlagsService: IFeatureFlagsService
    ) {
        super();
        this.user = this.api.getUser();
    }

    checkExpiry() {
        const [shouldExpireSession, earlyExpiryData] = this.getEarlyExpiryInformation();
        if (shouldExpireSession) {
            event(Event.AUTHENTICATION, `The current user's session was expired early. ${ JSON.stringify(earlyExpiryData) }`, this.user || undefined);
            if (this.user) {
                this.store.dispatch(DataActions.clearlastLoginDate(this.user.name));
            }

            this.api.forget();
        } else if (shouldAddExtraLogging(Source.USER)) {
            log(Source.USER, `Early session expiry check did not expire session. Blocked by: ${ earlyExpiryData.blockedBy }. ${ JSON.stringify(earlyExpiryData) }`, this.user || undefined);
        }
        return [shouldExpireSession, earlyExpiryData] as TEarlyExpiryInformation;
    }

    destroy() {
    }

    private getEarlyExpiryDate(lastLoginDate: Date) {
        const hasLongSesionExpiry = this.featureFlagsService.hasFeatureFlag(FeatureFlagName.LONG_SESSION_EXPIRY);
        // The early expiry date is the last login date plus the default expiry for session (14 days) minus the interval that we want to log the user out before the session expires (1 day)
        return new Date(lastLoginDate.getTime() + (hasLongSesionExpiry ? constants.SESSION.DEFAULT_LONG_EXPIRY : constants.SESSION.DEFAULT_EXPIRY) - constants.SESSION.AUTO_LOGOUT_BEFORE_EXPIRY);
    }

    private getEarlyExpiryInformation(): TEarlyExpiryInformation {
        // If the user is an IAM user, do not do early expiry as IAM manages sessions itself
        if (this.user?.iAMMetadata?.isIAMVerified) {
            return [false, {
                blockedBy: "iam-user"
            }];
        }

        const lastLoginDate = this.store.getState().data.lastLoginDate;
        const currentUserName = this.user?.name;
        let earlyExpiryDate: Date | undefined;
        if (!currentUserName
            || !lastLoginDate.hasOwnProperty(currentUserName)
            || (earlyExpiryDate = this.getEarlyExpiryDate(toDateTime(lastLoginDate[currentUserName]))).getTime() > getServerDate().getTime()) {
            // No current user or no login entry for the user or not currently after the early expiry date
            return [false, {
                earlyExpiryDate,
                blockedBy: "not-expired"
            }];
        }

        if (!this.userService.isDriverUsingUI(currentUserName)) {
            // The user that expired is not currently using the UI
            return [false, {
                earlyExpiryDate,
                blockedBy: "not-current-ui-user"
            }];
        }

        const mobileStore = this.store.getState().mobile;
        const currentPage = mobileStore.currentPage || "";
        const isLocked = mobileStore.pageState.isLocked;
        if (isLocked || protectedPagesRegex.test(currentPage)) {
            // Do not expire the session if the user is on the login, workflow or lock pages
            return [false, {
                earlyExpiryDate,
                blockedBy: "current-page"
            }];
        }

        const dutyStatusLogs = this.dutyStatusLogService.getSortedHosLogs(this.dutyStatusLogService.getHOSLogs(false).filter(statusLog => statusLog.isStandardStatusDriverLog()));
        const currentStatus = this.dutyStatusLogService.getCurrentDriverStatus(dutyStatusLogs);
        if (currentStatus === "D") {
            return [false, {
                earlyExpiryDate,
                blockedBy: "driving"
            }];
        }

        return [true, { earlyExpiryDate, currentPage, currentStatus }];
    }
}